import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../../lib/utils';

const buttonVariants = cva(
    'inline-flex items-center justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 min-h-[44px] min-w-[44px] text-center leading-none',
    {
        variants: {
            variant: {
                default: 'bg-primary text-primary-foreground shadow rounded-[12px] hover:opacity-90 active:translate-y-[1px]',
                secondary: 'bg-muted text-foreground rounded-[12px] hover:bg-muted/80',
                outline: 'border border-input bg-background rounded-[12px] hover:bg-muted',
                ghost: 'rounded-[12px] hover:bg-muted',
            },
            size: {
                default: 'h-11 px-5 py-2.5',
                sm: 'h-9 rounded-[12px] px-4 py-2',
                lg: 'h-11 rounded-[12px] px-8 py-2.5',
                icon: 'h-11 w-11',
            },
        },
        defaultVariants: {
            variant: 'default',
            size: 'default',
        },
    }
);

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(({
    className,
    variant,
    size,
    asChild = false,
    ...props
}, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
        <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
    );
});
Button.displayName = 'Button';

export { Button, buttonVariants };



